import React from 'react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { Music, Play, Download, Video, BookOpen, Building2, ArrowRight, ArrowLeft } from 'lucide-react';
import CorporateHeader from '../CorporateHeader';
import Footer from '../Footer';
import Breadcrumb from '../Breadcrumb';

const MindsetShiftMediaPage = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'soundtrack',
      title: 'The Official Soundtrack',
      description: '11 transformational R&B tracks designed to shift your mindset and elevate your journey. Includes exclusive lyric sheet PDF.',
      price: '$9.99',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png',
      cta: 'Purchase Soundtrack',
      path: '/mindset-shift-soundtrack',
      streamLink: 'https://distrokid.com/hyperfollow/egyptyoung/mindset-shift-the-official-soundtrack-2'
    },
    {
      id: 'book',
      title: 'The Mindset Shift Book',
      description: 'The foundational text that started the movement. Transform your thinking, transform your life.',
      price: 'Coming Soon',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/pjhwtif3_THE%20MINDSET%20SHIFT%20-%20cover.png',
      cta: 'Join Waitlist',
      path: null,
      comingSoon: true
    },
    {
      id: 'journal',
      title: 'True Companion Journal',
      description: 'Your personal growth companion. Guided journaling designed to reinforce daily mindset shifts.',
      price: 'Coming Soon',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/fap9i5s3_MINDSET%20SHIFT%20TRUE%20COMPANION%20JOURNAL.png',
      cta: 'Join Waitlist',
      path: null,
      comingSoon: true
    },
    {
      id: 'affirmations',
      title: 'Book of Daily Affirmations',
      description: 'Daily affirmations to reprogram your mindset and build unshakeable confidence.',
      price: 'Coming Soon',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/y4rkhenp_MINDSET%20SHIFT%20BOOK%20OF%20DAILY%20AFFIRMATIONS.png',
      cta: 'Join Waitlist',
      path: null,
      comingSoon: true
    },
    {
      id: 'workbook',
      title: 'Mindset Shift Workbook',
      description: 'Interactive workbook with exercises and activities to reinforce your mindset transformation.',
      price: 'Coming Soon',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/dmdu0u7i_MINDSET%20SHIFT%20WORKBOOK%20COVER.png',
      cta: 'Join Waitlist',
      path: null,
      comingSoon: true
    },
    {
      id: 'video-series',
      title: 'Jersey Mindset Auntie Video Series',
      description: 'Your guide to transformation. Daily videos that reinforce the mindset shifts you need to create real opportunity.',
      price: 'Free Access',
      image: 'https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/pjhwtif3_THE%20MINDSET%20SHIFT%20-%20cover.png',
      youtubeChannel: 'https://www.youtube.com/@JerseyMindsetAuntie',
      cta: 'Watch Now',
      externalLink: 'https://www.youtube.com/@JerseyMindsetAuntie',
    },
    {
      id: 'licensing',
      title: 'Corporate Licensing',
      description: 'Bring the Mindset Shift experience to your organization. Custom licensing packages for corporate training and events.',
      price: 'Custom Quote',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/iw87f00s_SHAREEN%20BACKDROP.jpeg',
      cta: 'Inquire',
      path: '/start-project',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      
      {/* Hero */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-purple-900/30 via-black to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-600/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <Breadcrumb items={[
            { label: 'Divisions', path: null },
            { label: 'Mindset Shift Media' }
          ]} />
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
              <Music className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-purple-400 text-sm font-semibold uppercase tracking-wider">Division</p>
              <h1 className="text-4xl lg:text-5xl font-black text-white">Mindset Shift Media</h1>
            </div>
          </div>
          
          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
            Transformational media for individuals and organizations. Music, books, video content, and corporate licensing designed to shift perspectives and unlock potential.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 max-w-5xl mx-auto">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div 
                  key={section.id}
                  className={`bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden hover:border-purple-600/50 transition-all duration-300 ${
                    section.comingSoon ? 'opacity-75' : ''
                  }`}
                >
                  <div className="grid md:grid-cols-3 gap-0">
                    {/* Image/Icon */}
                    <div className="aspect-square md:aspect-auto">
                      {section.image ? (
                        <img 
                          src={section.image} 
                          alt={section.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full min-h-[200px] bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
                          {Icon && <Icon className="w-20 h-20 text-white/30" />}
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="md:col-span-2 p-8 flex flex-col justify-center">
                      <h3 className="text-2xl font-bold text-white mb-3">{section.title}</h3>
                      <p className="text-gray-400 mb-4 leading-relaxed">{section.description}</p>
                      
                      <div className="flex items-center gap-4 mb-6">
                        <span className="text-2xl font-bold text-purple-400">{section.price}</span>
                      </div>
                      
                      <div className="flex flex-wrap gap-3">
                        {section.comingSoon ? (
                          <Button disabled className="bg-gray-700 text-gray-400 cursor-not-allowed">
                            Coming Soon
                          </Button>
                        ) : section.externalLink ? (
                          <a href={section.externalLink} target="_blank" rel="noopener noreferrer">
                            <Button className="bg-purple-600 hover:bg-purple-700 text-white font-semibold">
                              {section.cta}
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </a>
                        ) : (
                          <Button 
                            onClick={() => section.path && navigate(section.path)}
                            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                          >
                            {section.cta}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        )}
                        
                        {section.streamLink && (
                          <a href={section.streamLink} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" className="border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white">
                              <Play className="w-4 h-4 mr-2" />
                              Stream
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bundle CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-purple-900/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Get the Complete eCoSystem</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Access all Mindset Shift media, corporate licensing, and exclusive content in one comprehensive bundle.
          </p>
          <Button 
            onClick={() => navigate('/mindset-shift-ecosystem')}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-4 text-lg"
          >
            View Complete Bundle - $97
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default MindsetShiftMediaPage;
